import React, { useState, useEffect, createRef, useRef } from "react";
import { useTranslation } from "react-i18next";

import "@dotlottie/player-component";
import "./index.css";

export default function CompressedAnimationContainer(props) {
  const {
    t,
    // i18n
  } = useTranslation("translation");

  const player = createRef();

  useEffect(() => {
    // Mount player events
    if (player && player.current) {
      player.current.addEventListener(
        "error",
        () => {
          loadError();
        },
        false
      );
      player.current.addEventListener(
        "ready",
        (e) => {
          const animationData = e.target.getLottie();
          setFrameRate(animationData.frameRate);
          setTotalFrames(animationData.totalFrames);
          isLoaded(animationData.totalFrames, animationData.frameRate);
        },
        false
      );
      player.current.addEventListener(
        "frame",
        (event) => {
          newFrame(event);
        },
        false
      );
      player.current.addEventListener(
        "complete",
        () => {
          handleEnd();
        },
        false
      );
    }
  }, [player.current]);

  const myForcedProgressRef = useRef(props.forcedProgress);
  const [totalFrames, setTotalFrames] = useState(0);
  const [frameRate, setFrameRate] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [heightCalc, setHeightCalc] = useState("300vh");
  const [prevFrame, setPrevFrame] = useState(0);
  const activeLang = props.animLng;

  useEffect(() => {
    // Update frame with forced navigation

    if (props.forcedProgress !== null) {
      const frame = (props.forcedProgress / 100) * totalFrames;
      const newFrame = frame + props.adjustFrame;
      player.current.seek(parseFloat(newFrame.toFixed(2))); // Player requires decimals

      if (props.forceScrollUpdate) {
        correctScroll(frame);
      }
    }
  }, [props.forcedProgress]);

  useEffect(() => {
    // Re-calc view height to account for speed,, total frames and frame rate updates so that for each pixle is a frame + any buffers
    const val = (totalFrames * frameRate) / speed;
    setHeightCalc(`calc(300vh + ${1 + val}px)`);

    // return scrollEvent.removeEventListener("scroll");
  }, [speed, frameRate, totalFrames]);

  const loadError = () => {
    console.log("Animation error!");
  };

  const isLoaded = (total_frames, frame_rate) => {
    setSpeed(1);
    props.setTotalTime(total_frames / frame_rate);
    props.setAnimationReady();
    window.scrollTo(0, 0);
  };

  const newFrame = (event) => {
    const currentFrame = event.detail.frame;
    setPrevFrame(currentFrame);
    if (myForcedProgressRef !== null) {
      props.forceAnimationTo(null);
    } // reset after navigation
    props.updatePercentage(
      (currentFrame / event.target.getLottie().totalFrames) * 100
    );
  };

  const handleEnd = () => {
    props.updatePercentage(100);
  };

  const updateScroll = () => {
    // Update animation position with scroll if active
    if (props.scrollActive) {
      const elem = document.getElementById("animation-player");
      let scrollOffset =
        (elem.offsetTop - window.innerHeight) / (frameRate / speed);
      if (scrollOffset < 1) {
        scrollOffset = 0;
      }
      // eslint-disable-next-line no-empty
      if (scrollOffset == "Infinity") {
      } else if (scrollOffset > totalFrames) {
        player.current.seek(parseFloat(totalFrames.toFixed(2)));
      } else {
        player.current.seek(parseFloat(scrollOffset.toFixed(2)));
      }
    }
  };

  const correctScroll = (frame) => {
    // Update scroll position after navigation
    window.scrollTo(
      0,
      frame * (frameRate / speed) + (frameRate + 1) + window.innerHeight
    );
    props.forceScroll("reset");
  };

  const getTime = () => {
    // Conversion for usersnap element
    const percent = (prevFrame / totalFrames) * 100;
    const time = (percent / 100) * (totalFrames / frameRate);
    const minutes = Math.floor(time / 60);
    const seconds = (time - minutes * 60).toFixed(2);

    return `${minutes > 9 ? minutes : minutes > 0 ? `0${minutes}` : "00"}:${
      seconds > 9 ? seconds : seconds > 0 ? `0${seconds}` : "00.00"
    }`;
  };

  window.onscroll = function () {
    updateScroll();
  };

  return (
    <div
      id="animation-container"
      className="animation-container"
      role="group"
      aria-hidden={
        props.scrollActive === false
          ? true
          : props.ariaHideSplashScreens === "animation"
            ? false
            : true
      }
      aria-label={
        Number.isInteger(parseInt(props.currentDescriptionID) - 1) &&
        props.sceneData.Scrolly.descriptions[
          parseInt(props.currentDescriptionID) - 1
        ].props.altTxt !== ""
          ? t(
              `${
                props.sceneData.Scrolly.descriptions[
                  parseInt(props.currentDescriptionID) - 1
                ].props.altTxt
              }.string`
            )
          : t("contentArea.string")
      }
      style={{
        width: props.bp !== "lg" && props.bp !== "xl" ? "100%" : null,
      }}
    >
      {/* Debug section below */}
      {props.debug && props.previewAnimation ? (
        <div
          role="button"
          style={{
            position: "fixed",
            top: "36px",
            right: "0px",
            backgroundColor: "rgb(243, 86, 91)",
            borderRadius: "2px",
            padding: "6px",
            fontWeight: "bold",
            cursor: "pointer",
            zIndex: "99",
            minWidth: "50px",
            textAlign: "center",
          }}
          onClick={() => {
            if (speed === 1) {
              setSpeed(1.5);
            } else if (speed === 1.5) {
              setSpeed(2);
            } else if (speed === 2) {
              setSpeed(4);
            } else if (speed === 4) {
              setSpeed(0.1);
            } else if (speed === 0.1) {
              setSpeed(0.25);
            } else if (speed === 0.25) {
              setSpeed(0.5);
            } else {
              setSpeed(1);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              if (speed === 1) {
                setSpeed(1.5);
              } else if (speed === 1.5) {
                setSpeed(2);
              } else if (speed === 2) {
                setSpeed(4);
              } else if (speed === 4) {
                setSpeed(0.1);
              } else if (speed === 0.1) {
                setSpeed(0.25);
              } else if (speed === 0.25) {
                setSpeed(0.5);
              } else {
                setSpeed(1);
              }
            }
          }}
          tabIndex={0}
          aria-label={`Current speed: ${speed}`}
        >
          Speed: {speed}
        </div>
      ) : null}
      {props.debug && props.previewAnimation ? (
        <div
          style={{
            position: "fixed",
            top: "0px",
            right: "0px",
            backgroundColor: "rgb(243, 86, 91)",
            borderRadius: "2px",
            padding: "6px",
            fontWeight: "bold",
            zIndex: "99",
            minWidth: "50px",
            textAlign: "center",
            pointerEvents: "auto",
            cursor: "text",
          }}
        >
          Frame: {(prevFrame * 100) / 100} | Time: {getTime()}
        </div>
      ) : null}
      {/* Debug section above */}
      <div
        className="animation-scroll"
        id="animation-scroll"
        style={{ height: heightCalc }} // This height being set too large or changing after the animation data is loaded cause Axe testing tool to crash. When testing, manually set a value and fix before pushing the updates.
        role="group"
        aria-hidden="true"
      >
        <div
          id="animation-player"
          className={`animation-player ${
            props.bp !== "lg" && props.bp !== "xl"
              ? ""
              : "animation-player-desktop"
          }`}
          role="img"
        >
          <div aria-hidden="true" style={{ pointerEvents: "none" }}>
            <dotlottie-player // Main animation component
              // controls
              ref={player}
              loop={false}
              speed={speed}
              preserveAspectRatio="xMaxYMid meet"
              src={`locales/${activeLang}/animation.lottie`}
              style={{
                height:
                  props.bp === "lg" || props.bp === "xl"
                    ? "100vh"
                    : "calc(100vh - 200px)",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
